import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';

/**
 * Controla a exibição de um tour contextual (Cursos, Ranking, etc.).
 *
 * Dispara só quando:
 *  - o usuário ainda não viu esse tutorial específico, e
 *  - está na rota esperada
 *
 * Persistência via campo `user.tutorials_seen[key]` no backend.
 */
export function useContextualTour(key: string, expectedRoute: string) {
  const { user, completeTutorial } = useAuthContext();
  const location = useLocation();
  const [shouldStart, setShouldStart] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (location.pathname !== expectedRoute) return;

    const alreadySeen = Boolean(user.tutorials_seen?.[key]);
    if (alreadySeen) return;

    // Pequeno delay para o layout renderizar antes do spotlight
    const timer = setTimeout(() => setShouldStart(true), 800);
    return () => clearTimeout(timer);
  }, [user, location.pathname, key, expectedRoute]);

  const finishTour = async () => {
    setShouldStart(false);
    try {
      await completeTutorial(key);
    } catch (err) {
      console.error(`Falha ao marcar tutorial '${key}' como concluído`, err);
      // Não bloquear — o tour já encerrou localmente
    }
  };

  const skipTour = () => {
    void finishTour();
  };

  return { shouldStart, finishTour, skipTour };
}
